import ModalBase from "components/shared/ModalBase";
import PeriodName from "components/shared/PeriodName";
import type { IHandleRequest } from "hooks/useAPIPagination";
import { useTranslation } from "react-i18next";
import type { GeneralTabTerm } from "store/managementStore";
import type { TPeriod } from "store/periodStore";

import AdvanceUpdateOption from "./AdvanceUpdateOption";
import BalanceOverrideOption from "./BalanceOverrideOption";

interface IBalanceUpdateModal {
    term: GeneralTabTerm | null;
    option: "override" | "advance";
    setModalType: (value: React.SetStateAction<string>) => void;
    currentPeriod: TPeriod | undefined;
    /**
     * Triggers animation on balance cell.
     */
    setBalanceUpdateSuccessful: React.Dispatch<
        React.SetStateAction<{
            termId: number | null;
            animate: boolean;
        }>
    >;
    /**
     * When set (e.g. from Management), runs the same refetch as `useAPIPagination` `handleRequest` for
     * the "terms" tab. Otherwise AdvanceUpdateOption falls back to `refetchManagementGeneralTab`.
     */
    refetchGeneralTab?: (params?: IHandleRequest) => void;
}

/**
 * Modal for updating balance from Management page.
 * Two cases exist: either overriding current balance or updating advance details.
 */
const BalanceUpdateModal = ({
    term,
    option,
    setModalType,
    currentPeriod,
    setBalanceUpdateSuccessful,
    refetchGeneralTab,
}: IBalanceUpdateModal) => {
    const [t] = useTranslation();

    const isOverride = option === "override";

    const getModalTitle = () => {
        if (isOverride) {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: " 0.3rem" }}>
                    <span>{t("BalanceUpdate.OptionsEdit")}</span>
                    <PeriodName
                        period={currentPeriod}
                        textStyle={{ fontSize: "16px", fontWeight: "400" }}
                        containerStyle={{ alignItems: "flex-end" }}
                    />
                </div>
            );
        } else {
            return term?.hasAdvances ? t("BalanceUpdate.OptionsUpdateAdvance") : t("BalanceUpdate.CreateAdvance");
        }
    };

    const getModalSubtitle = () => {
        if (isOverride) {
            return t("BalanceUpdate.OptionsEditDescription");
        } else {
            return term?.hasAdvances ? t("BalanceUpdate.AdvanceDetail") : t("BalanceUpdate.CreateAdvanceDesc");
        }
    };

    return (
        <ModalBase
            open={["override", "advance"].includes(option)}
            onClose={() => setModalType("")}
            modalTitle={getModalTitle()}
            modalSubtitle={getModalSubtitle()}
            widthTransition={{ active: true, width: isOverride ? "870px" : "1100px" }}
        >
            {isOverride ? (
                <BalanceOverrideOption
                    term={term}
                    handleClose={() => setModalType("")}
                    setBalanceUpdateSuccessful={setBalanceUpdateSuccessful}
                />
            ) : (
                <AdvanceUpdateOption
                    term={term}
                    handleClose={() => setModalType("")}
                    setBalanceUpdateSuccessful={setBalanceUpdateSuccessful}
                    refetchGeneralTab={refetchGeneralTab}
                />
            )}
        </ModalBase>
    );
};

export default BalanceUpdateModal;
